import React, { useMemo, useState } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import debounce from 'lodash/debounce';
import { withStyles } from '@material-ui/core';

const styles = {
  container: {
    '&& label': {
      display: 'block',
      padding: '12px 15px',
      border: '1px solid rgba(178,178,178,0.5)',
      borderRadius: 3,
    },
    '&& label > input': {
      color: '#212322',
      fontFamily: 'Roboto',
      fontSize: 14,
      border: 'none',
      width: '100%',
    },
  },
};

const Form = ({ classes, onChange }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [fields, setFields] = useState({
    cardNumber: { error: true, complete: false },
    cardExpiry: { error: true, complete: false },
    cardCvc: { error: true, complete: false },
    cardOwner: { error: true },
  });

  const isInvalid = (nextFields = fields) => {
    return Object.entries(nextFields).reduce((prev, [, { error }]) => {
      return prev || !!error;
    }, false);
  };

  const handleSubmit = async(nextFields = fields) => {
    if (!stripe || !elements || isInvalid(nextFields)) {
      onChange(null, stripe || null);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      onChange(null, stripe || null);
      return;
    }

    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
      billing_details: {
        name: nextFields.cardOwner?.value || '',
      },
    });

    if (result.error) {
      onChange(null, stripe);
      return;
    }

    onChange(result.paymentMethod.id, stripe);
  };

  const debouncedChange = useMemo(
    () =>
      debounce((nextFields) => {
        handleSubmit(nextFields).catch(() => {
          onChange(null, stripe || null);
        });
      }, 1000),
    [stripe, elements],
  );

  const handleChange = (event) => {
    const nextFields = {
      ...fields,
      [event.elementType]: {
        error: !!event.error || !event.complete,
        complete: !!event.complete,
      },
    };

    setFields(nextFields);

    if (isInvalid(nextFields)) {
      onChange(null, stripe || null);
    }

    debouncedChange(nextFields);
  };

  const handleNameChange = (event) => {
    const { name, value } = event.target;

    const nextFields = {
      ...fields,
      [name]: {
        value,
        error: !value.trim(),
      },
    };

    setFields(nextFields);

    if (isInvalid(nextFields)) {
      onChange(null, stripe || null);
    }

    debouncedChange(nextFields);
  };

  return (
    <form className={classes.container}>
      <Grid container spacing={3}>
        <Grid item xs={10}>
          <label>
            <Typography>Name on a card</Typography>
            <input
              type="text"
              name="cardOwner"
              data-testid="card-owner"
              onChange={handleNameChange}
            />
          </label>
        </Grid>

        <Grid item xs={10}>
          <label>
            <Typography>Card number</Typography>
            <CardNumberElement
              data-testid="card-number"
              onChange={handleChange}
            />
          </label>
        </Grid>

        <Grid item xs={5}>
          <label>
            <Typography>Card expiration date</Typography>
            <CardExpiryElement
              data-testid="card-expiration-date"
              onChange={handleChange}
            />
          </label>
        </Grid>

        <Grid item xs={5}>
          <label>
            <Typography>Card CVC number</Typography>
            <CardCvcElement
              data-testid="card-cvc-number"
              onChange={handleChange}
            />
          </label>
        </Grid>
      </Grid>
    </form>
  );
};

export default withStyles(styles)(Form);
